import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Indicator, DataOptions, GeoLevel, TimeRangeMap } from '../types';
import { getMinimumTimerange } from '../get-minimumtimerange';
import {
    getFilterLanCode,
    getLanTimeRangeMap,
    getSelectedLanCode,
} from '../lan-coverage/lan-coverage';

const initialGeoFilterIds = import.meta.env.VITE_KOMMUNER
    .split(',')
    .filter(Boolean);

function getFilterGeoIdString(geoFilterIds: string[]) {
    return geoFilterIds.map((geoId) => `filterGeoId=${geoId}`).join('&');
}

export type AreaProperties = {
    id: string;
    geoId: string;
    name: string;
    kommunnamn: string;
    labelCoords?: [number, number];
};

export type DataPoint = {
    type: 'Feature';
    properties: AreaProperties & {
        values: Record<string, any>;
        [key: string]: any; // Allow additional properties for flexibility
    };
    geometry: any; // Compatible with Point, Polygon, or null
};

export type Data = {
    properties: Map<string, AreaProperties>;
    indicators: Map<string, Map<string, any>>;
    points: DataPoint[]; // Compatible array format for existing code
    polygons: { type: 'FeatureCollection'; features: DataPoint[] }; // For backwards compatibility
    options: DataOptions;
    hasInterpolatedValues: boolean;
};

async function fetchProperties(
    level: GeoLevel,
    geoFilterIds: string[],
    year?: number
): Promise<AreaProperties[]> {
    const yearParam = year !== undefined ? `&year=${year}` : '';
    const url = `${
        import.meta.env.VITE_API_URL
    }/api/v1/data/${level}_properties.json?${getFilterGeoIdString(geoFilterIds)}${yearParam}`;
    const request = await fetch(url);
    if (!request.ok) {
        let message = `fetchProperties failed: HTTP ${request.status}`;
        try {
            const body = await request.json();
            if (body?.error) message = body.error;
        } catch {
            // ignore parse errors
        }
        throw new Error(message);
    }
    const json = await request.json();
    return json;
}

async function fetchTable(
    url: string,
    valueKey: string
): Promise<{ map: Map<string, any>; hasInterpolatedValues: boolean }> {
    const request = await fetch(url, {
        credentials: 'include',
    });
    const text = await request.text();
    const rows = text.match(/.+/g)?.map((row: string) => JSON.parse(row)) ?? [];

    let hasInterpolatedValues = false;
    const map = new Map<string, any>();
    rows.forEach((row) => {
        if (row.id && row.values) {
            // Extract the specific indicator values using valueKey
            const indicatorValues = row.values[valueKey];
            if (row.values.hasInterpolatedValues) {
                hasInterpolatedValues = true;
            }
            if (indicatorValues) {
                map.set(row.id, indicatorValues);
            }
        }
    });

    return { map, hasInterpolatedValues };
}

export async function getData(
    options: DataOptions,
    indicatorsMap: Record<string, Indicator>,
    geoFilterIds: string[] = initialGeoFilterIds
): Promise<Data> {
    // Build timeRangeMap from indicatorsMap
    const timeRangeMap: TimeRangeMap = Object.values(options.indicators).reduce(
        (map, indicator) => {
            if (indicator?.timeRanges) {
                map[indicator.id] = indicator.timeRanges;
            }
            return map;
        },
        {} as TimeRangeMap
    );

    // Get minimum time range across all indicators
    const indicators = Object.values(options.indicators).filter(
        (i) => i !== null
    ) as Indicator[];
    const timeRange = getMinimumTimerange({
        indicators,
        geoLevel: options.geoLevel,
        timeRangeMap: getLanTimeRangeMap(
            timeRangeMap,
            getSelectedLanCode(geoFilterIds)
        ),
    });

    // Extract latest year from timeRange
    const latestYear = timeRange[1];

    // Only fetch indicator data for the län being shown
    const lanCode = getFilterLanCode(geoFilterIds);

    // Fetch base properties for all areas (including 2025 versions if year >= 2024)
    // and the indicator tables in parallel
    const [propertiesArray, tables] = await Promise.all([
        fetchProperties(options.geoLevel, geoFilterIds, latestYear),
        Promise.all(
            indicators.map((indicator) => {
                const url = indicator.urls[options.geoLevel];
                const tableUrl = lanCode
                    ? `${url}${url.includes('?') ? '&' : '?'}lan=${lanCode}`
                    : url;
                return fetchTable(tableUrl, indicator.valueKey);
            })
        ),
    ]);

    // Convert to Map for easy lookup (keyed by geoId)
    const propertiesMap = new Map<string, AreaProperties>();
    propertiesArray.forEach((props) => {
        propertiesMap.set(props.geoId, props);
    });

    // Create indicator data Maps
    const indicatorsDataMap = new Map<string, Map<string, any>>();

    let hasInterpolatedValues = false;

    for (const [index, indicator] of indicators.entries()) {
        const {
            map: indicatorValuesMap,
            hasInterpolatedValues: _hasInterpolatedValues,
        } = tables[index];

        if (_hasInterpolatedValues) {
            hasInterpolatedValues = _hasInterpolatedValues;
        }

        // Store the Map for this indicator
        if (indicatorValuesMap.size > 0) {
            indicatorsDataMap.set(indicator.id, indicatorValuesMap);
        }
    }

    if (propertiesMap.size === 0) {
        throw new Error('fetchProperties returned an empty array');
    }

    // Build compatible points array for existing code
    const points: DataPoint[] = Array.from(propertiesMap.entries()).map(
        ([geoId, props]) => {
            const values: Record<string, any> = {};
            indicatorsDataMap.forEach((indicatorMap, indicatorId) => {
                const areaValues = indicatorMap.get(geoId);
                if (areaValues) {
                    values[indicatorId] = areaValues;
                }
            });

            return {
                type: 'Feature' as const,
                properties: {
                    ...props,
                    values,
                },
                geometry: null,
            };
        }
    );

    return {
        properties: propertiesMap,
        indicators: indicatorsDataMap,
        points,
        polygons: { type: 'FeatureCollection' as const, features: points },
        options,
        hasInterpolatedValues,
    };
}

export function useDataQuery(
    targetOptions: DataOptions,
    indicatorsMap: Record<string, Indicator>,
    geoFilterIds: string[] = initialGeoFilterIds
) {
    const allIndicatorsExistInMap = Object.values(
        targetOptions.indicators
    ).reduce((value, indicator) => {
        if (indicator === null) return true;
        if (!value) return value;
        return !!indicatorsMap[indicator?.id];
    }, true);

    if (!allIndicatorsExistInMap) {
        return null
    }
    return useQuery({
        queryKey: ['data', targetOptions, geoFilterIds],
        queryFn: () => getData(targetOptions, indicatorsMap, geoFilterIds),
        placeholderData: keepPreviousData,
    });
}

// Helper: Get all properties as an array
export function getPropertiesArray(data: Data): AreaProperties[] {
    return Array.from(data.properties.values());
}

// Helper: Get indicator value for a specific area and year
export function getIndicatorValue(
    data: Data,
    indicatorId: string,
    geoId: string,
    year: number
): number | undefined {
    return data.indicators.get(indicatorId)?.get(geoId)?.[year];
}

// Helper: Get all indicator values for an area
export function getAreaIndicatorValues(
    data: Data,
    geoId: string
): Record<string, any> {
    const values: Record<string, any> = {};
    data.indicators.forEach((indicatorMap, indicatorId) => {
        const areaValues = indicatorMap.get(geoId);
        if (areaValues) {
            values[indicatorId] = areaValues;
        }
    });
    return values;
}
